import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client/core';

import { createHttpLink } from '@apollo/client/link/http';
import { ApolloLink } from '@apollo/client/link/core/ApolloLink';

export type InitClientParams = {
  apiBase?: string;
  apiEndpoint?: string;
  appId?: string;
  apiApp?: string;
  gqlApiAppId?: string;
  gqlApiApp?: string;
  // Endpoint for the internal (VP staff) schema. Optional: it defaults to
  // `apiEndpoint`, since both surfaces are served from the same /graphql path
  // today. ADR-009's future runtime extraction changes only this value.
  internalApiEndpoint?: string;
  // What the request sends alongside itself. Omitted, Apollo leaves `fetchOptions.credentials`
  // unset and the browser applies `same-origin` — right for the bearer-token clients, wrong for
  // the internal one, whose gate authenticates with a signed session cookie and which ADR-009
  // contemplates moving to a host of its own.
  //
  // Only `createApiClient` reads this. `InitClientParams` doubles as the parameter type of
  // `connectionStore.init()`, which copies a fixed set of keys, so passing it there is silently
  // ignored.
  // Spelled out rather than `RequestCredentials`: that is a DOM lib global, and this package's
  // ESLint env does not declare one.
  credentials?: 'omit' | 'same-origin' | 'include';
  middleware?: ApolloLink[];
};

export const createApiClient = (options: InitClientParams): ApolloClient<NormalizedCacheObject> => {
  const endpoint = options.apiEndpoint || `${options.apiBase}/graphql`;

  const httpLink = createHttpLink({
    uri: endpoint,
    credentials: options.credentials,
  });

  const cache = new InMemoryCache();

  let link;
  if (options.middleware) {
    link = ApolloLink.from([
      ...options.middleware,
      httpLink,
    ]);
  } else {
    link = httpLink;
  }

  return new ApolloClient({
    link,
    cache,
    queryDeduplication: true,
  });
};
